import type { Suggestion } from '@ts-internal/shared-lib';
import { useState } from 'react';

interface Props {
  intialValues?: {
    roleTitle?: string[];
    matchLatestRoleTitle?: boolean;
    location?: string[];
    company?: string[];
    matchLatestCompany?: boolean;
  };
}

export const useFilters = ({ intialValues }: Props) => {
  // Role Title
  const roleTitleInitialValues = mapToSuggestions(
    intialValues?.roleTitle ?? [],
  );
  const [selectedRoleTitleItems, setSelectedRoleTitleItems] = useState<
    Suggestion[]
  >(roleTitleInitialValues);

  const [matchLatestRoleTitleOnlyState, setMatchLatestRoleTitleOnlyState] =
    useState(intialValues?.matchLatestRoleTitle ?? false);

  // Location
  const locationInitialValues = parseLocationUrlToSuggestions(
    intialValues?.location ?? [],
  );
  const [selectedLocationItems, setSelectedLocationItems] = useState<
    Suggestion[]
  >(locationInitialValues);

  // Company
  const companyInitialValues = mapToSuggestions(intialValues?.company ?? []);
  const [selectedCompanyItems, setSelectedCompanyItems] =
    useState<Suggestion[]>(companyInitialValues);

  const [matchLatestCompanyOnlyState, setMatchLatestCompanyOnlyState] =
    useState(intialValues?.matchLatestCompany ?? false);

  // UI states
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);

  const reset = () => {
    setSelectedRoleTitleItems([]);
    setSelectedLocationItems([]);
    setMatchLatestRoleTitleOnlyState(false);
    setSelectedCompanyItems([]);
    setMatchLatestCompanyOnlyState(false);
  };

  const getFilterItemsCount = () =>
    // add more filters here
    selectedRoleTitleItems.length +
    selectedLocationItems.length +
    (matchLatestRoleTitleOnlyState ? 1 : 0) +
    selectedCompanyItems.length +
    (matchLatestCompanyOnlyState ? 1 : 0);

  return {
    roleTitle: {
      selectedItems: selectedRoleTitleItems,
      selectedItemRawStrings: selectedRoleTitleItems.map((item) => item.name),
      setSelectedItems: setSelectedRoleTitleItems,
      matchLatestRoleTitle: matchLatestRoleTitleOnlyState,
      setMatchLatestRoleTitle: setMatchLatestRoleTitleOnlyState,
    },
    location: {
      selectedItems: selectedLocationItems,
      selectedItemRawStrings: selectedLocationItems.map(
        (item) => `${item.name} ${item.id}`,
      ),
      setSelectedItems: setSelectedLocationItems,
    },
    company: {
      selectedItems: selectedCompanyItems,
      selectedItemRawStrings: selectedCompanyItems.map((item) => item.name),
      setSelectedItems: setSelectedCompanyItems,
      matchLatestCompany: matchLatestCompanyOnlyState,
      setMatchLatestCompany: setMatchLatestCompanyOnlyState,
    },
    mobilePanelState: {
      isOpen: isMobilePanelOpen,
      setIsOpen: setIsMobilePanelOpen,
    },
    filterItemsCount: getFilterItemsCount(),
    reset,
  };
};

const mapToSuggestions = (names: string[]): Suggestion[] =>
  names.map((name) => ({
    id: `custom-${generateRandomId()}`,
    name,
  }));

const parseLocationUrlToSuggestions = (
  locationsFromUrl: string[],
): Suggestion[] =>
  locationsFromUrl.map((location) => {
    const parts = location.split(' ');
    const id = parts.pop() ?? generateRandomId();
    const locationName = parts.join(' ');

    return {
      id,
      name: locationName,
    };
  });

const generateRandomId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2);
